import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { createCommentReaction, createReaction, deleteReaction } from '@sola/my-sdk/content-feed/reactions.ts';
import type { ContentFeedReactionEmoji, ContentFeedReactionSummary } from '@sola/schema/dotcom/proxy-api/content-feed.ts';
import { trackContentFeedLike } from '@sola/tracking/components/datalayer.tsx';
export type ReactionEmoji = ContentFeedReactionEmoji;

/** Number of reaction badges shown in the summary row. */
export const summaryReactionsDisplayLimit = 3;

/**
 * Returns the reactions with the highest counts, in descending order, capped at
 * `limit`, excluding zero/negative counts. Does not mutate the input array.
 */
export const getTopReactions = (reactions: ContentFeedReactionSummary[] | undefined, limit: number = summaryReactionsDisplayLimit): ContentFeedReactionSummary[] => {
  if (!reactions?.length) {
    return [];
  }
  return [...reactions].filter(reaction => reaction.count > 0).sort((a, b) => b.count - a.count).slice(0, limit);
};

/**
 * Placeholder id for an optimistically added reaction whose create request is
 * still in flight. It marks the emoji as selected in the picker before the
 * server assigns a real id, and is either replaced on success or discarded by
 * the rollback on failure — it must never be sent to the API.
 */
const pendingReactionId = 'pending';

/**
 * Accessible label key per approved emoji. Typed against the emoji union so
 * adding an emoji to the schema fails the build until a label is supplied.
 */
const emojiLabelKeys: Record<ContentFeedReactionEmoji, string> = {
  '🎉': 'my:contentFeed.reactionLabelCelebrate',
  '❤️': 'my:contentFeed.reactionLabelLove',
  '🙌': 'my:contentFeed.reactionLabelPraise',
  '🙏': 'my:contentFeed.reactionLabelGrateful',
  '😍': 'my:contentFeed.reactionLabelAdore',
  '👍': 'my:contentFeed.reactionLabelLike'
};
export const emojiAnalyticsNames: Record<ContentFeedReactionEmoji, string> = {
  '🎉': 'Celebrate',
  '❤️': 'Love',
  '🙌': 'Praise',
  '🙏': 'Grateful',
  '😍': 'Adore',
  '👍': 'Like'
};
export interface UseContentFeedReactionsProps {
  entryId?: string;
  /** When provided, reactions target a comment instead of an entry */
  commentId?: string;
  /** Title of the parent entry — sent as `content_title` in the like event */
  contentTitle?: string;
  /** Landing-page URL of the parent entry — sent as `content_url` in the like event */
  contentUrl?: string;
  /** Initial reaction summaries from the entry response */
  reactions?: ContentFeedReactionSummary[];
  /** Whether reactions are enabled for this entry */
  canReact?: boolean;
  /** Called whenever the optimistic reactions state changes (e.g. after add/remove/switch) */
  onReactionsChange?: (reactions: ContentFeedReactionSummary[]) => void;
  /** Called when a reaction is just added/switched, with the emoji (or null when animation ends) */
  onJustReacted?: (emoji: string | null) => void;
}
export const useContentFeedReactions = ({
  entryId,
  commentId,
  contentTitle,
  contentUrl,
  reactions: initialReactions = [],
  canReact = true,
  onReactionsChange,
  onJustReacted
}: UseContentFeedReactionsProps) => {
  const {
    t
  } = useTranslation(['my']);
  const [reactions, setReactions] = useState<ContentFeedReactionSummary[]>(initialReactions);
  const [isPickerOpen, setIsPickerOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [justReactedEmoji, setJustReactedEmoji] = useState<string | null>(null);
  const justReactedTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Notify parent when optimistic reactions state changes
  const isInitialMount = useRef(true);
  useEffect(() => {
    // Skip the initial render — only fire for actual user-driven changes
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    onReactionsChange?.(reactions);
  }, [reactions, onReactionsChange]);

  // Notify parent of the just-reacted emoji for animation
  useEffect(() => {
    onJustReacted?.(justReactedEmoji);
  }, [justReactedEmoji, onJustReacted]);

  // Clear the "just reacted" highlight after the animation plays
  useEffect(() => {
    return () => {
      if (justReactedTimer.current) {
        clearTimeout(justReactedTimer.current);
      }
    };
  }, []);

  // Find the current user's reaction (if any)
  const userReaction = reactions.find(r => !!r.userReactionId);
  const totalCount = reactions.reduce((sum, r_0) => sum + r_0.count, 0);

  // Highest-count reactions for the summary row (non-mutating).
  const topReactions = getTopReactions(reactions);
  const togglePicker = useCallback(() => {
    setIsPickerOpen(prev => !prev);
  }, []);
  const closePicker = useCallback(() => {
    setIsPickerOpen(false);
  }, []);
  const handleSelectEmoji = useCallback(async (emoji: ReactionEmoji) => {
    if (isSubmitting) {
      return;
    }
    setIsPickerOpen(false);
    setIsSubmitting(true);

    // Track which emoji was just added for the summary pop animation
    const triggerJustReacted = (reactionEmoji: string) => {
      setJustReactedEmoji(reactionEmoji);
      if (justReactedTimer.current) {
        clearTimeout(justReactedTimer.current);
      }
      justReactedTimer.current = setTimeout(() => setJustReactedEmoji(null), 500);
    };

    // Snapshot for rollback — every optimistic path below restores this on
    // failure so a failed request can't leave a drifted count or a
    // permanently pending reaction id behind.
    const previousReactions = reactions;

    // If user already reacted with this emoji, remove it
    if (userReaction?.emoji === emoji) {
      const reactionId = userReaction.userReactionId;

      // Optimistic: remove user's reaction
      setReactions(prev_0 => prev_0.map(r_1 => r_1.emoji === emoji ? {
        ...r_1,
        count: r_1.count - 1,
        userReactionId: undefined
      } : r_1).filter(r_2 => r_2.count > 0));
      try {
        if (reactionId && reactionId !== pendingReactionId) {
          await deleteReaction(reactionId);
        }
      } catch (err) {
        setReactions(previousReactions);
        console.error('[reactions] delete failed:', err);
      } finally {
        setIsSubmitting(false);
      }
      return;
    }

    // If user reacted with a different emoji, remove old first
    if (userReaction) {
      const oldReactionId = userReaction.userReactionId!;
      triggerJustReacted(emoji);

      // Optimistic: remove old, add new
      setReactions(prev_1 => {
        const withoutOld = prev_1.map(r_3 => r_3.emoji === userReaction.emoji ? {
          ...r_3,
          count: r_3.count - 1,
          userReactionId: undefined
        } : r_3).filter(r_4 => r_4.count > 0);
        const existingNew = withoutOld.find(r_5 => r_5.emoji === emoji);
        if (existingNew) {
          return withoutOld.map(r_6 => r_6.emoji === emoji ? {
            ...r_6,
            count: r_6.count + 1,
            userReactionId: pendingReactionId
          } : r_6);
        }
        return [...withoutOld, {
          emoji,
          count: 1,
          userReactionId: pendingReactionId
        }];
      });
      try {
        // Create the new reaction first. If this fails, nothing changed
        // server-side and the rollback to previousReactions is accurate.
        const result = commentId ? await createCommentReaction(commentId, emoji) : await createReaction(entryId!, emoji);
        setReactions(prev_2 => prev_2.map(r_7 => r_7.emoji === emoji && r_7.userReactionId === pendingReactionId ? {
          ...r_7,
          userReactionId: result.id
        } : r_7));

        // Now delete the old reaction. If this fails, the user has two
        // reactions momentarily — the API enforces one-per-entry, so the
        // old one becomes orphaned but the UI is in the correct state.
        if (oldReactionId && oldReactionId !== pendingReactionId) {
          try {
            await deleteReaction(oldReactionId);
          } catch (deleteErr) {
            // Non-critical: the create succeeded, UI is correct. Log and move
            // on — the server may garbage-collect or reject the stale entry.
            console.error('[reactions] old reaction cleanup failed:', deleteErr);
          }
        }
      } catch (err_0) {
        setReactions(previousReactions);
        setJustReactedEmoji(null);
        console.error('[reactions] switch failed:', err_0);
      } finally {
        setIsSubmitting(false);
      }
      return;
    }

    // No existing reaction — create new
    triggerJustReacted(emoji);
    setReactions(prev_3 => {
      const existing = prev_3.find(r_8 => r_8.emoji === emoji);
      if (existing) {
        return prev_3.map(r_9 => r_9.emoji === emoji ? {
          ...r_9,
          count: r_9.count + 1,
          userReactionId: pendingReactionId
        } : r_9);
      }
      return [...prev_3, {
        emoji,
        count: 1,
        userReactionId: pendingReactionId
      }];
    });
    try {
      const result_0 = commentId ? await createCommentReaction(commentId, emoji) : await createReaction(entryId!, emoji);
      setReactions(prev_4 => prev_4.map(r_10 => r_10.emoji === emoji && r_10.userReactionId === pendingReactionId ? {
        ...r_10,
        userReactionId: result_0.id
      } : r_10));
      if (entryId) {
        trackContentFeedLike({
          contentId: entryId,
          contentTitle: contentTitle || '',
          contentUrl,
          emoji: emojiAnalyticsNames[emoji],
          commentId
        });
      }
    } catch (err_1) {
      setReactions(previousReactions);
      setJustReactedEmoji(null);
      console.error('[reactions] create failed:', err_1);
    } finally {
      setIsSubmitting(false);
    }
  }, [entryId, commentId, contentTitle, contentUrl, isSubmitting, reactions, userReaction]);
  const getEmojiLabel = useCallback((emoji_0: ReactionEmoji) => t(emojiLabelKeys[emoji_0]), [t]);
  return {
    reactions,
    userReaction,
    totalCount,
    topReactions,
    isPickerOpen,
    isSubmitting,
    canReact,
    justReactedEmoji,
    togglePicker,
    closePicker,
    handleSelectEmoji,
    getEmojiLabel,
    t
  };
};